'use client'

import {
  $autoinspectorApi,
  AutoinspectorApi,
  AutoinspectorApiPaths,
} from '@autoinspector/providers/autoinspector'
import { IdentityProvider, RetrieveGuestResponseBody, SignInMethod } from 'bff'
import { GoogleLogo } from 'components/GoogleLogo'
import { MicrosoftLogo } from 'components/MicrosoftLogo'
import { useTranslation } from 'i11n/client'
import { useCallback } from 'react'
import { Button } from 'ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'ui/accordion'
import { ImageAutoinspectorLogo } from 'components/ImageAutoinspectorLogo'
import { Badge } from 'ui/badge'
import { cn } from 'ui/utils'

export type SignMethod = 'idp' | 'email'

export const SignInShellMoreOptions = ({
  setSignMethod,
  lastSignInMethod,
}: {
  setSignMethod: (method: SignMethod) => void
  lastSignInMethod?: SignInMethod | null
}) => {
  const t = useTranslation()

  return (
    <Accordion className='border-0' type='single' collapsible>
      <AccordionItem className='border-0' value='item-1'>
        <AccordionTrigger
          chevronIconClassName='text-violet-500 !w-3 !h-3'
          className='no-underline flex flex-row items-center justify-center w-78 py-2 focus:no-underline hover:underline'
        >
          <span className='text-slate-500 font-space-grotesk text-sm font-medium leading-5 mr-[0.25rem]'>
            {t('Más opciones')}
          </span>
        </AccordionTrigger>

        <AccordionContent className='pt-[1.5rem] pb-2'>
          <div className='relative'>
            {lastSignInMethod === 'email' && <LastSignInBadge />}
            <button
              onClick={() => setSignMethod('email')}
              className='flex w-[22.4375rem] h-[2.875rem] p-3 justify-center items-center gap-[0.625rem] rounded-xl border-[1.2px] border-solid border-violet-500 bg-white hover:border-violet-700'
            >
              <span className='text-slate-700 text-center font-space-grotesk text-sm font-medium'>
                {t('Continuar con email')}
              </span>
            </button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export const LastSignInBadge = () => {
  const t = useTranslation()

  return (
    <div className='absolute -top-3 z-10 w-full flex justify-end'>
      <Badge className='bg-white text-xs' variant='ghost'>
        {t('Último ingreso')}
      </Badge>
    </div>
  )
}

export const SignInShellSocialProviders = <
  T extends '/v1/account/auth/oauth/authorization/url',
>({
  endpoint,
  params,
  authConfig,
  lastSignInMethod,
}: {
  endpoint: T
  params: Omit<
    AutoinspectorApiPaths['accountService'][T]['post']['requestBody']['content']['application/json'],
    'idp'
  >
  authConfig: RetrieveGuestResponseBody['company']['authConfig']
  lastSignInMethod?: SignInMethod | null
}) => {
  const t = useTranslation()
  const mutation = $autoinspectorApi.useMutation('post', endpoint, {
    //@ts-ignore
    onSuccess: (data) => {
      //@ts-ignore
      window.location.href = data.url
    },
  })

  const handleSocialSignIn = useCallback(
    (provider: IdentityProvider) => {
      //@ts-ignore
      mutation.mutate({
        body: {
          ...params,
          idp: provider,
        },
      })
    },
    [params],
  )

  return (
    <div className='flex flex-col space-y-4'>
      {authConfig.google && (
        <div className='relative'>
          {lastSignInMethod === 'google' && <LastSignInBadge />}
          <Button
            onClick={() => handleSocialSignIn('google')}
            className='text-base w-full bg-primary text-white hover:bg-primary hover:text-white h-[2.87rem] border-0 rounded-[.625rem]'
            size='lg'
          >
            <GoogleLogo className='size-4' fill='white' stroke='white' />
            <span className='text-white font-space-grotesk text-base font-medium ml-[0.5rem]'>
              {t('Continuar con Google')}
            </span>
          </Button>
        </div>
      )}

      {authConfig.microsoft && (
        <div className='relative'>
          {lastSignInMethod === 'microsoft' && <LastSignInBadge />}

          <Button
            onClick={() => handleSocialSignIn('microsoft')}
            className='text-base w-full bg-primary text-white hover:bg-primary hover:text-white h-[2.87rem] border-0 rounded-[.625rem]'
            size='lg'
          >
            <MicrosoftLogo className='size-4' fill='white' stroke='white' />
            <span className='text-white font-space-grotesk text-base font-medium ml-[0.5rem]'>
              {t('Continuar con Microsoft')}
            </span>
          </Button>
        </div>
      )}
    </div>
  )
}

export const SignInShellHeader = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <div className={cn('text-left space-y-4', className)}>{children}</div>
}

export const SignInShellHeaderDescription = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <p className='text-slate-700 font-space-grotesk text-base font-medium leading-5'>
      {children}
    </p>
  )
}

export const SignInShellHeaderTitle = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <h1
      className={cn(
        'text-slate-700 font-space-grotesk text-[2rem] font-bold leading-[1.75rem] mt-4',
        className,
      )}
    >
      {children}
    </h1>
  )
}

export const SignInShell = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className={cn(
        'h-svh w-full flex flex-col md:overflow-y-hidden overflow-x-hidden bg-white relative',
      )}
    >
      <style>
        {`
        body {
        background: #ffffff;
        }
        `}
      </style>
      <div className='w-full h-full p-6 flex items-center justify-center z-10'>
        <div className='max-w-4xl w-full h-full'>
          <div className='h-full flex items-center justify-center flex-col'>
            <div className='animate-fadein'>
              <div className='w-[23rem] flex flex-col'>
                <ImageAutoinspectorLogo />
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
